import * as THREE from 'three';
import {
    BossExtrudeSceneResolver,
    type BossExtrudeSceneContour,
    type BossExtrudeSelectionResolution,
} from '../extrude/BossExtrudeSceneResolver';
import type { RevolveFeaturePrototype } from '../../prototype/revolve/RevolveFeaturePrototype';
import {
    RevolveAxisResolver,
    type BossRevolveAxisResolution,
    type BossRevolveSessionAxisSelection,
    type RevolveModelEdgeSegmentResolver,
} from './RevolveAxisResolver';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责把场景选择解析为旋转凸台创建/编辑会话可消费的领域对象；
 * 3. 该类只做场景解析编排，不构建命令、不执行预览。
 *
 * 文件职责：
 * 1. 复用 ProfileDriven 特征已有的草图轮廓解析链路；
 * 2. 将旋转轴解析委托给 RevolveAxisResolver；
 * 3. 为 Viewer 和 SessionBridge 提供旋转语义命名的统一入口。
 */

/**
 * 旋转凸台场景轮廓。
 */
export type BossRevolveSceneContour = BossExtrudeSceneContour;

/**
 * 旋转凸台轮廓选择解析结果。
 */
export type BossRevolveSelectionResolution = BossExtrudeSelectionResolution;

/**
 * 旋转凸台场景解析器。
 */
export class BossRevolveSceneResolver {
    /** 草图轮廓解析器。 */
    private readonly profileResolver = new BossExtrudeSceneResolver();
    /** 旋转轴解析器。 */
    private readonly axisResolver = new RevolveAxisResolver();

    /**
     * 根据对象 ID 解析旋转轮廓。
     *
     * @param scene 当前场景。
     * @param objectId 选中对象 ID。
     * @returns 轮廓解析结果。
     */
    public resolveContoursFromSelection(
        scene: THREE.Scene | null,
        objectId: string,
    ): BossRevolveSelectionResolution {
        return this.profileResolver.resolveContoursFromSelection(scene, objectId);
    }

    /**
     * 从旋转 prototype 恢复轮廓列表。
     *
     * @param scene 当前场景。
     * @param prototype 旋转特征原型。
     * @returns 已恢复轮廓集合。
     */
    public resolveContoursFromPrototype(
        scene: THREE.Scene | null,
        prototype: RevolveFeaturePrototype,
    ): BossRevolveSceneContour[] {
        const profileBinding = prototype
            .getBindings()
            .map((binding) => binding.serialize())
            .find((binding) => binding.kind === 'sketch-profile');
        const contourKeys = profileBinding?.kind === 'sketch-profile'
            ? (profileBinding.contourKeys ?? []).filter((item): item is string => typeof item === 'string')
            : [];
        const sketchId = profileBinding?.kind === 'sketch-profile'
            ? profileBinding.sketchId
            : prototype.getSourceSketchId();

        /**
         * 旋转与拉伸共用同一条 `sketch-profile` 绑定协议，
         * 草图身份归一与轮廓回查统一委托给 profile 解析器，避免两处各自维护匹配规则。
         */
        return this.profileResolver.resolveContoursFromProfileBinding(scene, sketchId, contourKeys);
    }

    /**
     * 根据对象 ID 与拓扑命中解析旋转轴。
     *
     * @param input 解析输入。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    public resolveAxisFromSelection(input: {
        scene: THREE.Scene | null;
        objectId: string;
        topologyPick?: ResolvedTopologyPick | null;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): BossRevolveAxisResolution | null {
        const selection: BossRevolveSessionAxisSelection = {
            objectId: input.objectId,
            topologyPick: input.topologyPick ?? null,
        };
        return this.axisResolver.resolveFromSelection({
            scene: input.scene,
            selection,
            resolveModelEdgeSegment: input.resolveModelEdgeSegment,
        });
    }
}
